import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { Component } from '../component';
import { styles } from './close-button.styles';

import '../icon';

export type CloseButtonSize = 'small' | 'default' | 'large';

export interface CloseButtonProps {
	controls?: string;
	disabled?: boolean;
	inverted?: boolean;
	label?: string;
	size?: CloseButtonSize;
}

export class CloseButton extends Component {
	static override styles = [styles()];

	static override get properties() {
		return {
			controls: { type: String },
			disabled: { type: Boolean },
			inverted: { type: Boolean },
			label: { type: String },
			size: { type: String },
		};
	}

	/**
	 * Id of the element being controlled by the close button.
	 */
	declare controls: CloseButtonProps['controls'];

	/**
	 * Determine if the button will be disabled.
	 */
	declare disabled: CloseButtonProps['disabled'];

	/**
	 * Determine if the button will be displayed with inverted colors.
	 */
	declare inverted: CloseButtonProps['inverted'];

	/**
	 * Button label. Defaults to 'Close'.
	 */
	declare label: CloseButtonProps['label'];

	/**
	 * Text field size. Defaults to 'default'.
	 */
	declare size: CloseButtonProps['size'];

	constructor() {
		super();
		this.disabled = false;
		this.inverted = false;
		this.label = 'Close';
		this.size = 'default';
	}

	override render() {
		return html`
			<button
				type="button"
				aria-controls="${ifDefined(this.controls)}"
				aria-label="${ifDefined(this.label)}"
				class=${classMap({
					'close-button': true,

					'-size-small': this.size === 'small',
					'-size-default': this.size === 'default',
					'-size-large': this.size === 'large',
					'-inverted': this.inverted ?? false,
				})}
				?disabled="${this.disabled}"
				@click=${this.#handleClick}
			>
				<m-icon name="close"></m-icon>
			</button>
		`;
	}

	#handleClick = () => {
		this.emit('m-close');
	};
}

window.customElements.define('m-close-button', CloseButton);

declare global {
	interface HTMLElementTagNameMap {
		'm-close-button': CloseButton;
	}
}
